/**
 * Notice — the one shared status line every surface speaks a failure or a piece of news through
 * (plan `2026_09_01_notice_component.md` §3, operator decision 2026-09-01: "failures are standard
 * notices, not bespoke states"). Four kinds, one line of text, said once and cleared after `ttlMs`
 * unless the caller passes 0.
 *
 * The kind sets the colour only, from CSS variables (`--notice-info|success|warning|error`) each
 * product's root stylesheet defines from its own tokens (toile `styles/index.scss`, narcis
 * `styles/partials/colors.scss`, ovid `styles/index.scss`) — this component carries no colour
 * literal of its own. `className` is the surface's placement class (position and type only, e.g.
 * the mono tracking of the wall's own note slots) — never colour, which the kind already owns.
 *
 * Not a toast: no stacking, no dismiss button, no multi-line message, no link inside the line. A
 * notice that needs any of those is not a notice (plan §2) — reach for a full component instead
 * (`TosConsentCard`, `SignupGate`, `MusePickStep`).
 */
import {useEffect, useRef} from 'react'
import classNames from 'classnames'
import styles from './Notice.module.scss'

export type NoticeKind = 'info' | 'success' | 'warning' | 'error'

export type NoticeProps = {
    kind: NoticeKind
    /** The one line — lowercase, no punctuation, in the surface's own vocabulary. */
    children: string
    /** ms until the line clears itself by calling `onClear`; 0 = stays until the parent removes
     *  it (e.g. the page verb's line, which waits on the next press). Default mirrors the wall's
     *  own `NOTICE_MS` (`core/wall/ui/Wall.tsx`) — said once, 8s. */
    ttlMs?: number
    onClear?: () => void
    /** The surface's placement class — position and type only, never colour. */
    className?: string
}

/** How long a notice with no `ttlMs` of its own stays up before clearing itself. */
const DEFAULT_TTL_MS = 8_000

export function Notice({kind, children, ttlMs = DEFAULT_TTL_MS, onClear, className}: NoticeProps) {
    const onClearRef = useRef(onClear)
    onClearRef.current = onClear

    useEffect(() => {
        if (!ttlMs) return
        const timer = setTimeout(() => onClearRef.current?.(), ttlMs)
        return () => clearTimeout(timer)
        // Restarts whenever a new line arrives, same contract as the wall's own notice timer.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [children, ttlMs])

    return (
        <p role="status" aria-live="polite" data-kind={kind} className={classNames(styles.notice, className)}>
            {children}
        </p>
    )
}
